import { useQuery } from "@tanstack/react-query";
import { engineError } from "@/lib/engine-error";
import type { ListEnvelope } from "@/lib/profitability-api";
import { bookKeys } from "@/lib/query";

/**
 * How much of an Aggregate user's book the selected range covers: accounts
 * with activity in it, or distinct projects. Before search and filters, so the
 * "Your book" line describes the book on screen, not whatever the search box
 * narrowed it to -- and not `access.book_accounts`, which counts every account
 * in NOS, dormant ones included (60 against 18 listed, for one partner).
 *
 * A one-row request: the envelope's totals are what is wanted. Only sent when
 * the page already knows the caller is Aggregate.
 */
export function useBookCount(kind: "accounts" | "projects", range: string, enabled: boolean) {
  return useQuery({
    queryKey: bookKeys.count(kind, range),
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<number> => {
      const params = new URLSearchParams({ limit: "1" });
      if (range) params.set("range", range);
      const response = await fetch(`/api/${kind}?${params.toString()}`);
      if (!response.ok) throw await engineError(response, "Could not count your book");
      const body = (await response.json()) as ListEnvelope<unknown>;
      return kind === "projects" ? (body.distinct_projects ?? body.total) : body.total;
    },
  });
}
